/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import {
  CALCULATOR_METAQUESTION_QUESTION_INDEX,
  pageCanHaveNextLink,
  questionCanHaveOptions,
  questionCanHaveToggleableRequired,
} from './runAlgorithm';
import {
  PageOptionExclusiveType,
  PageOptionMultipleChoiceType,
  PageQuestionExclusiveType,
  PageQuestionFreeTextType,
  PageQuestionMultipleChoiceType,
  PageQuestionNumericalEntryType,
  PageQuestionUploadPictureType,
  PageCalculatorType,
  PageMultipleQuestionsType,
  PageTypeEnum,
  PageType,
  QuestionType,
  SelectedButtonInfo,
  LinkSlugType,
} from './types';

export function getKeyForMultipleChoiceOption(option: PageOptionMultipleChoiceType): string {
  if (!option) return '';
  return (option.multiple_choice_key || option.text || '').toUpperCase();
}

export function getValueForMultipleChoiceOption(option: PageOptionMultipleChoiceType) {
  return 'YES';
}

export function getPossibleValuesForMultipleChoiceOption(option: PageOptionMultipleChoiceType) {
  if (!option) return '';
  return 'YES,NO';
}

export function getKeyForExclusiveQuestion(
  question: PageQuestionExclusiveType,
  page: PageMultipleQuestionsType | PageCalculatorType
) {
  return (question.exclusive_key || getLegacyKeyForExclusiveQuestion(question, page)).toUpperCase();
}

/** This is the legacy fallback from before exclusive_key was used consistently.
 *  It is still useful when migrating old documents that used display text as the variable id.
 */
export function getLegacyKeyForExclusiveQuestion(
  question: PageQuestionExclusiveType,
  page: PageMultipleQuestionsType | PageCalculatorType
) {
  if (page.questions!.length === 1) {
    // When there is only one question, we don't have a 'question' string, and we use the page name instead
    // as the fallback
    return page.name || '';
  }
  return question.question || '';
}

/** For other questions, just use the id
 *  The value is: `selectedKeys[key].value`
 */
export function getKeyForQuestionOther(
  question: PageQuestionFreeTextType | PageQuestionNumericalEntryType | PageQuestionUploadPictureType
) {
  return (question.id || '').toUpperCase();
}

export function fixupKey(key: string, page: PageType, questionIndex: number) {
  if (key || (page.pageType !== PageTypeEnum.MULTIPLE_QUESTIONS && page.pageType !== PageTypeEnum.CALCULATOR))
    return key.toUpperCase();
  const question = page.questions?.[questionIndex]!;
  if (question.type !== 'exclusive') {
    throw new Error('Missing key for multiple choice question');
  }
  return getKeyForExclusiveQuestion(question, page);
}

/** Find AND TOGGLE the button
 *  It's not very nice to have both finding and toggling in one function, but it's a bit tricky to separate them.
 *
 *  We want to toggle ONLY if the new value equal the old value, AND if it is not None AND it is mutually exclusive
 */
export function findAndToggleButtonInfo(
  page: PageMultipleQuestionsType | PageCalculatorType,
  messageIndex: number,
  key: string,
  value: string,
  originalCaseValue: string, // In the free text-input case only, we use the original case
  toggleValue: boolean // Note, ignored if the option is isNone
): SelectedButtonInfo | undefined {
  const isCalc = page.pageType === PageTypeEnum.CALCULATOR;
  let questionIndex = -1;
  if (!page.questions) return;
  for (const question of page.questions) {
    questionIndex += 1;
    if (question.type === 'exclusive') {
      const stableKey = getKeyForExclusiveQuestion(question, page);
      const legacyKey = getLegacyKeyForExclusiveQuestion(question, page).toUpperCase();
      if (stableKey === key || legacyKey === key) {
        let optionIndex = -1;
        for (const option of question.options) {
          optionIndex += 1;
          if (getValueForExclusiveOption(option) === value) {
            return {
              value, // Note that we don't toggle exclusive
              summarytext: getSummaryText(question, option),
              isNone: option.isNone,
              messageIndex,
              questionIndex,
              optionIndex,
              ...(isCalc ? { points: option.points, hideScore: page.hideScores } : {}),
            };
          }
        }
      }
    } else if (question.type === 'multiple_choice') {
      let optionIndex = -1;
      if (questionCanHaveOptions(question)) {
        for (const option of question.options) {
          optionIndex += 1;
          if (key === getKeyForMultipleChoiceOption(option)) {
            const newValue = option.isNone || !toggleValue ? value : value === 'NO' ? 'YES' : 'NO';
            return {
              value: newValue,
              summarytext: getSummaryText(question, option, newValue === 'NO'),
              isNone: option.isNone,
              messageIndex,
              questionIndex,
              optionIndex,
              ...(isCalc ? { points: newValue !== 'NO' ? option.points : undefined, hideScore: page.hideScores } : {}),
            };
          }
        }
      }
    } else if (question.type === 'numerical_entry') {
      // Can't toggle a numerical entry, so this is a bit of a misnomer, but we do set it
      if (question.id?.toUpperCase() === key) {
        if (value) {
          value = roundToStep(parseFloat(value), question.step || 0.1).toString();
        }
        return {
          value,
          summarytext: getSummaryText(question, undefined, undefined, value == null ? '(N/A)' : value),
          messageIndex,
          questionIndex,
        };
      }
    } else if (question.type === 'free_text') {
      // Can't toggle a text entry, so this is a bit of a misnomer
      if (question.id?.toUpperCase() === key) {
        return {
          value: originalCaseValue,
          summarytext: getSummaryText(
            question,
            undefined,
            undefined,
            originalCaseValue ? `'${originalCaseValue}'` : '(N/A)'
          ),
          messageIndex,
          questionIndex,
        };
      }
    }
  }
}

export function roundToStep(value: number, step: number) {
  return Math.round(value / step) * step;
}

export function isButtonSelected(
  question: PageQuestionExclusiveType | PageQuestionMultipleChoiceType,
  page: PageType | undefined,
  option: PageOptionMultipleChoiceType | PageOptionExclusiveType | undefined,
  selectedKeys: { [key: string]: SelectedButtonInfo }
) {
  if (!option) return false;
  if (!page || (page.pageType !== PageTypeEnum.MULTIPLE_QUESTIONS && page.pageType !== PageTypeEnum.CALCULATOR))
    return false;
  if (question.type === 'multiple_choice') {
    const key = getKeyForMultipleChoiceOption(option);
    return selectedKeys[key]?.value === getValueForMultipleChoiceOption(option);
  } else if (question.type === 'exclusive') {
    const key = getKeyForExclusiveQuestion(question, page);
    return selectedKeys[key]?.value === getValueForExclusiveOption(option);
  }
  return false;
}

export function getValueForExclusiveOption(option: PageOptionExclusiveType) {
  if (!option) return '';
  return (option.exclusive_value || option.text || '').toUpperCase();
}

export function getSummaryText(
  question: QuestionType,
  option?: PageOptionExclusiveType | PageOptionMultipleChoiceType,
  negate?: boolean,
  valueStr?: string
): { keyStr: string; valueStr: string } {
  if (negate && question.negativeSummarytextPrefix) {
    // This is where we want an output like:
    // Patient admits having: Rash
    // Patient denies having: Fever
    // Where 'negativeSummarytextPrefix' is 'Patient denies having'
    // In this case, we don't negate the option
    return {
      keyStr: question.negativeSummarytextPrefix,
      valueStr: valueStr || option?.summarytext || option?.text || '',
    };
  }
  const keyStr = question.summarytextPrefix || question.question || '';
  if (option) {
    if (negate && option.negativesummarytext && !valueStr) {
      return { keyStr, valueStr: valueStr || option.negativesummarytext };
    }
    valueStr ||= option.summarytext || option.text;
  }
  if (negate) {
    return { keyStr, valueStr: `Not ${valueStr}` };
  }

  return { keyStr, valueStr: valueStr || '' };
}
export function getNegatedButtonInfoForOption(
  question: PageQuestionMultipleChoiceType,
  option: PageOptionMultipleChoiceType,
  messageIndex: number,
  questionIndex: number,
  optionIndex: number,
  hideFromSummary: boolean
): SelectedButtonInfo {
  return {
    value: 'NO',
    summarytext: getSummaryText(question, option, true),
    messageIndex,
    questionIndex,
    optionIndex,
    hideFromSummary,
  };
}

export function getLinkForExclusiveOption(
  option: PageOptionExclusiveType,
  page: PageMultipleQuestionsType | PageCalculatorType
): LinkSlugType | undefined {
  if (!option) return undefined;
  if (option.link) return { link: option.link?.trim(), link_slug: option.linkSlug };
  // When we have only one question and it is a single choice question, and NOT a calculator,
  // then use the text as the link value.
  // TBH This was a bad design decision, and we should have stuck to the link field
  // It would be nice to backfill the existing documents, then remove this behaviour
  if (
    page.pageType === PageTypeEnum.MULTIPLE_QUESTIONS &&
    page.questions!.length === 1 &&
    page.questions![0]!.type === 'exclusive' &&
    !page.next_link
  ) {
    return { link: option.text?.trim(), link_slug: undefined };
  }
  return undefined;
}

export function getLinkForMultipleChoiceOption(option: PageOptionMultipleChoiceType) {
  const link = option?.link?.trim();
  if (link) return { link, link_slug: option.linkSlug };
  return undefined;
}

function compareSelectedButtonInfo(buttonInfo1: SelectedButtonInfo, buttonInfo2: SelectedButtonInfo) {
  return (
    buttonInfo1.messageIndex - buttonInfo2.messageIndex ||
    buttonInfo1.questionIndex! - buttonInfo2.questionIndex! ||
    // Clump all the non-No (Yes) buttons together, then all the No buttons
    (buttonInfo1.value === 'NO' && buttonInfo2.value !== 'NO'
      ? 1
      : buttonInfo1.value !== 'NO' && buttonInfo2.value === 'NO'
      ? -1
      : // Otherwise sort by the order they were in the UI
        0) ||
    buttonInfo1.optionIndex! - buttonInfo2.optionIndex!
  );
}

/** Just used to make it easier to to unit test choicesToList */
export function choicesToStr(
  selectedKeys: { [key: string]: SelectedButtonInfo },
  showPoints?: boolean,
  showOnlyMessageIndex?: number
) {
  return choicesToList(selectedKeys, showPoints, showOnlyMessageIndex)
    .map(
      (x) =>
        `${x.summarytext.keyStr}${x.summarytext.keyStr && x.summarytext.valueStr ? ': ' : ''}${x.summarytext.valueStr}`
    )
    .join(',\n');
}

export function choicesToList(
  selectedKeys: { [key: string]: SelectedButtonInfo },
  showPoints?: boolean,
  showOnlyMessageIndex?: number
): SelectedButtonInfo[] {
  return (
    Object.keys(selectedKeys)
      .sort((key1, key2) => compareSelectedButtonInfo(selectedKeys[key1]!, selectedKeys[key2]!))
      .filter((key) => {
        const selected = selectedKeys[key]!;
        return (
          !selected!.hideFromSummary &&
          (selected!.summarytext.keyStr || selected!.summarytext.valueStr) &&
          (showOnlyMessageIndex === undefined || selected.messageIndex === showOnlyMessageIndex) &&
          selected.questionIndex! < CALCULATOR_METAQUESTION_QUESTION_INDEX + (selected.hideScore ? 0 : +1) &&
          ((selected.questionIndex! === CALCULATOR_METAQUESTION_QUESTION_INDEX && showOnlyMessageIndex != null) ||
            selected!.summarytext.keyStr)
        );
      })
      // Reduce to give a list like [ { keyStr: 'Pets', valueStr: 'Cat, Dog', messageIndex: 2, .. } ]
      .reduce((acc, key) => {
        const x = selectedKeys[key]!;
        const last = acc[acc.length - 1];

        let valueStr = x!.summarytext.valueStr;
        if (showPoints && x.questionIndex! < CALCULATOR_METAQUESTION_QUESTION_INDEX && x!.points && !x!.hideScore) {
          if (x!.points > 0) {
            valueStr += ` (+${x!.points})`;
          } else {
            valueStr += ` (${x!.points})`;
          }
        }

        if (last && last.messageIndex === x!.messageIndex && last.summarytext.keyStr === x!.summarytext.keyStr) {
          valueStr = `${last.summarytext.valueStr}, ${valueStr}`;
          acc[acc.length - 1] = {
            ...last,
            summarytext: { ...last.summarytext, valueStr },
            points: undefined,
          };
        } else {
          acc.push({
            ...x,
            summarytext: { ...x.summarytext, valueStr },
          });
        }
        return acc;
      }, [] as SelectedButtonInfo[])
  );
}

export type QuestionKeyValueValuesType = {
  [key: string]: string; // key to human-readable text.  The human-readable text is just for creator UI
};

export type QuestionKeyValueType = {
  values: QuestionKeyValueValuesType | true; // value to human-readable text.  The human-readable text is just for creator UI
  questionText: string;
  optionText: string;
  options?: PageOptionMultipleChoiceType[];
};
/** This is a fairly weird class.
 *  The idea is that you can add key values to it, but the values can either be
 *  arbitrary strings, like:  "AGE" = "<40>"
 *  or the values can be just a true/false.  Like:  "ISPREGNANT".
 *
 *  To represent this, we have a map of keys to either:
 *   - An object QuestionKeyValueValuesType    representing the list of possible arbitrary values like {key1: "TEEN", key2: "ADULT", key3: "OAP"]
 *   - or, true         representing that this can be true or false
 *
 *  See the question_utils.test.js for how it should work.
 */
export class KeyValuesSet {
  data: Map<string, QuestionKeyValueType>;

  constructor() {
    this.data = new Map<string, QuestionKeyValueType>();
  }

  addYesNo(key: string, questionText: string, optionText: string, options?: PageOptionMultipleChoiceType[]): this {
    if (!this.data.has(key)) {
      this.data.set(key, { values: true, questionText, optionText, options });
    } else if (this.data.get(key)?.values !== true) {
      this.add(key, 'YES', questionText, optionText, options);
      this.add(key, 'NO', questionText, optionText, options);
    }
    return this;
  }

  add(
    key: string,
    value: string,
    questionText: string,
    optionText: string,
    options?: PageOptionMultipleChoiceType[]
  ): this {
    if (!this.data.has(key)) {
      this.data.set(key, { values: {}, questionText: '', optionText: questionText, options });
    } else if (this.data.get(key)?.values === true) {
      if (value === 'YES' || value === 'NO') {
        if (options) {
          this.data.get(key)!.options = options;
        }
        return this;
      }
      this.data.set(key, {
        values: { YES: optionText, NO: optionText },
        questionText: '',
        optionText: this.data.get(key)?.optionText || '',
        options,
      });
    }

    (this.data.get(key)!.values as QuestionKeyValueValuesType)[value] = optionText;
    this.data.get(key)!.options = options;
    return this;
  }

  hasKey(key: string): boolean {
    return !!this.data.has(key);
  }

  has(key: string, value: string): boolean {
    const values = this.data.get(key)?.values;
    if (values === undefined) return false;
    if (values === true) return value === 'YES' || value === 'NO';
    return (values as QuestionKeyValueValuesType).hasOwnProperty(value);
  }

  keys() {
    return this.data.keys();
  }

  values(key: string) {
    return this.data.get(key);
  }

  size() {
    return this.data.size;
  }
}

export function getAllKeyValuesForQuestion(
  question: QuestionType,
  page: PageMultipleQuestionsType | PageCalculatorType,
  keysFound = new KeyValuesSet()
) {
  const questionText = question.summarytextPrefix || question.question || page.name;
  if (questionCanHaveToggleableRequired(question) && !question.required) {
    // e.g. free_text, numerical_entry, upload_picture
    // If they aren't required, have a YES/NO option to say whether the user entered them or not
    keysFound.addYesNo(`has_${question.id}`, questionText, `has been answered`);
  }
  if (!questionCanHaveOptions(question)) {
    return keysFound;
  }
  const options = question.options;
  if (question.type === 'exclusive') {
    const key = getKeyForExclusiveQuestion(question, page);
    options.forEach((option) => {
      const value = getValueForExclusiveOption(option);
      keysFound.add(key, value, questionText, option.text);
    });
  } else if (question.type === 'multiple_choice') {
    options.forEach((option) => {
      const value = getValueForMultipleChoiceOption(option);
      const key = getKeyForMultipleChoiceOption(option);
      if (value === 'YES') keysFound.addYesNo(key, questionText, option.text, options);
      else keysFound.add(key, value, questionText, option.text, options);
    });
  }
  return keysFound;
}

export function getAllKeyValuesForMultipleQuestions(
  page: PageMultipleQuestionsType | PageCalculatorType,
  keysFound = new KeyValuesSet()
) {
  page.questions?.forEach((question) => getAllKeyValuesForQuestion(question, page, keysFound));
  return keysFound;
}

export function getNextPageLinkForPage(page: PageType) {
  if (pageCanHaveNextLink(page) && page.next_link) {
    return { link: page.next_link?.trim(), link_slug: page.next_link_slug };
  }
  return undefined;
}

export function getReturnToHereInfo(page: PageType | undefined) {
  if (!page) return undefined;
  if (page.pageType === PageTypeEnum.MULTIPLE_QUESTIONS || page.pageType === PageTypeEnum.CALCULATOR) {
    if (page.returnToHere?.text) {
      return page.returnToHere;
    }
  }
  return undefined;
}

/** This function isn't really useful outside of a unit test, but it checks the page to see if a multiple choice question has
 *  a key that matches the keyOrValue.  If it does, then it returns the key and value of the option.
 *  And if the page has a single choice question, then it checks if the keyOrValue matches one of the values,
 *  and returns the key and value of that option.
 */
export function getPageQuestionKeyOrValue(page: PageType, keyOrValue: string) {
  if (page.pageType === PageTypeEnum.MULTIPLE_QUESTIONS || page.pageType === PageTypeEnum.CALCULATOR) {
    if (!page.questions) return undefined;
    for (const question of page.questions) {
      if (question.type === 'multiple_choice') {
        if (!question.options) continue;
        for (const option of question.options) {
          if (getKeyForMultipleChoiceOption(option) === keyOrValue) {
            return { key: keyOrValue, value: getValueForMultipleChoiceOption(option) };
          }
        }
      } else if (question.type === 'exclusive') {
        if (!question.options) continue;
        for (const option of question.options) {
          if (getValueForExclusiveOption(option) === keyOrValue) {
            return { key: getKeyForExclusiveQuestion(question, page), value: keyOrValue };
          }
        }
      }
    }
  }
  return undefined;
}
